import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const ServiceDetails = ({ activeTab: initialTab }) => {
  const [activeTab, setActiveTab] = useState("ai");

  // Update active tab when prop changes
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  // Service data with detailed descriptions
  const services = [
    {
      id: "ai",
      title: "AI & Digital Transformation",
      description:
        "Advanced AI solutions, custom GPTs, and digital innovation strategies to drive meaningful business outcomes.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z"
            clipRule="evenodd"
          />
        </svg>
      ),
      bullets: [
        "Design and implement AI-powered business solutions and workflows",
        "Develop custom GPTs and AI agents for specific business needs",
        "Create digital transformation strategies and implementation roadmaps",
        "Establish AI governance frameworks and ethical guidelines",
        "Deliver AI skills workshops and organizational readiness programs",
      ],
      industries: ["Healthcare", "Financial Services", "Technology", "Retail"],
    },
    {
      id: "strategic",
      title: "Strategic Planning & Execution",
      description:
        "Tailored roadmaps, governance frameworks, and strategic advisory to align vision with execution.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
        </svg>
      ),
      bullets: [
        "Develop comprehensive strategic frameworks and roadmaps",
        "Establish governance and operational excellence models",
        "Facilitate strategic planning sessions and leadership workshops",
        "Align corporate vision with practical execution plans",
        "Design and implement balanced scorecards and measurement frameworks",
      ],
      industries: [
        "Healthcare",
        "Public Sector",
        "Technology",
        "Financial Services",
      ],
    },
    {
      id: "product",
      title: "Product Management & Marketing",
      description:
        "Comprehensive product strategy, UX refinement, and go-to-market execution to maximize product success.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
        </svg>
      ),
      bullets: [
        "Develop product strategies and roadmaps aligned with business objectives",
        "Create user-centered design frameworks and UX optimization",
        "Establish product metrics and measurement systems",
        "Design and execute comprehensive go-to-market strategies",
        "Conduct product management training and capability building",
      ],
      industries: [
        "SaaS",
        "Healthcare Technology",
        "Consumer Products",
        "FinTech",
      ],
    },
    {
      id: "program",
      title: "Program & Project Delivery",
      description:
        "End-to-end program oversight, agile implementation, and risk management for successful project execution.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
            clipRule="evenodd"
          />
        </svg>
      ),
      bullets: [
        "Manage complex program delivery and multi-project portfolios",
        "Implement agile methodologies and develop hybrid delivery frameworks",
        "Establish project governance and risk management protocols",
        "Lead organizational change management and stakeholder engagement",
        "Provide interim program management leadership",
      ],
      industries: [
        "Public Sector",
        "Healthcare",
        "Enterprise Technology",
        "Financial Services",
      ],
    },
    {
      id: "cloud",
      title: "Cloud & Technical Consulting",
      description:
        "Cloud migration, multi-cloud strategy, and infrastructure optimization to modernize your technology stack.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M5.5 16a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 16h-8z" />
        </svg>
      ),
      bullets: [
        "Develop cloud strategy and migration roadmaps",
        "Design multi-cloud and hybrid cloud architectures",
        "Implement infrastructure optimization and modernization",
        "Establish cloud governance and security frameworks",
        "Provide technical architecture and solution design services",
      ],
      industries: [
        "Healthcare",
        "Financial Services",
        "Public Sector",
        "Technology",
      ],
    },
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const activeService = services.find((service) => service.id === activeTab);

  return (
    <section className="pt-24 pb-20 bg-dark relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-40 -left-20 w-96 h-96 bg-accent/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-0 w-80 h-80 bg-accent/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-light mb-4">
            Our <span className="text-accent">Services</span>
          </h2>
          <p className="text-light/80 text-lg max-w-3xl mx-auto">
            With experience across multiple industries, Eluno Consulting
            delivers tailored solutions to address your unique business
            challenges.
          </p>
        </div>

        {/* Service tabs wrapper */}
        <div className="mb-12">
          {/* First row (3 tabs) */}
          <div className="flex flex-wrap justify-center gap-2 mb-3">
            {services.slice(0, 3).map((service) => (
              <button
                key={service.id}
                onClick={() => handleTabChange(service.id)}
                className={`px-4 py-2 rounded-full text-sm md:text-base font-medium transition-colors duration-300 ${
                  activeTab === service.id
                    ? "bg-accent text-dark"
                    : "bg-dark/50 text-light/70 border border-light/10 hover:border-accent/30"
                }`}
              >
                <span className="flex items-center">
                  <span className="mr-2">{service.icon}</span>
                  {service.title}
                </span>
              </button>
            ))}
          </div>

          {/* Second row (2 tabs) */}
          <div className="flex flex-wrap justify-center gap-2">
            {services.slice(3, 5).map((service) => (
              <button
                key={service.id}
                onClick={() => handleTabChange(service.id)}
                className={`px-4 py-2 rounded-full text-sm md:text-base font-medium transition-colors duration-300 ${
                  activeTab === service.id
                    ? "bg-accent text-dark"
                    : "bg-dark/50 text-light/70 border border-light/10 hover:border-accent/30"
                }`}
              >
                <span className="flex items-center">
                  <span className="mr-2">{service.icon}</span>
                  {service.title}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Service detail */}
        <div className="bg-dark/50 border border-light/10 rounded-xl p-8 md:p-10 transition-all duration-500">
          <div className="grid md:grid-cols-5 gap-10">
            <div className="md:col-span-3">
              <h3 className="text-2xl md:text-3xl font-bold text-light mb-4 flex items-center">
                <span className="text-accent mr-3">{activeService.icon}</span>
                {activeService.title}
              </h3>

              <p className="text-light/80 text-lg mb-6">
                {activeService.description}
              </p>

              <h4 className="text-xl font-semibold text-light mb-4">
                How We Help
              </h4>
              <ul className="space-y-3 mb-8">
                {activeService.bullets.map((bullet, index) => (
                  <li key={index} className="flex items-start">
                    <svg
                      className="h-6 w-6 text-accent flex-shrink-0 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-light/70">{bullet}</span>
                  </li>
                ))}
              </ul>

              <Link
                to={`/contact?service=${activeService.id}`}
                className="inline-flex items-center px-5 py-3 bg-accent text-dark font-medium rounded-lg hover:bg-accent/90 transition-colors duration-300"
              >
                <span>Contact Us About This Service</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 ml-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </Link>
            </div>

            <div className="md:col-span-2">
              <div className="bg-dark/70 border border-light/10 rounded-lg p-6">
                <h4 className="text-xl font-semibold text-light mb-4">
                  Industry Experience
                </h4>
                <div className="flex flex-wrap gap-2 mb-6">
                  {activeService.industries.map((industry, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-accent/10 border border-accent/20 rounded-full text-accent text-sm"
                    >
                      {industry}
                    </span>
                  ))}
                </div>

                <h4 className="text-xl font-semibold text-light mb-4">
                  Approach
                </h4>
                <p className="text-light/70 mb-4">
                  We combine deep industry knowledge with specialized expertise
                  to deliver tailored solutions that address your unique
                  challenges.
                </p>
                <p className="text-light/70">
                  Our collaborative approach ensures we understand your specific
                  needs and develop strategies that align with your business
                  objectives.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceDetails;
